import { chunk, not } from '@skylab-shared/utils';
import { v4 as uuid } from 'uuid';
import { EventBus, IntervalEndedData, IntervalEvents, IntervalStartedData } from '../../event';

export interface TaskQueueConfig<TRecord> {
    readonly interval?: number;
    readonly enableBatching?: boolean;
    readonly batchSize?: number;
    duplicateDetectionComparer?(a: TRecord, b: TRecord): boolean;
}

export type ProcessingFunction<TRecord> = (record: TRecord | TRecord[], done: () => void) => void;

export class TaskQueue<TRecord> {
    private readonly processingFunction: ProcessingFunction<TRecord>;
    private readonly config: Required<TaskQueueConfig<TRecord>>;
    private readonly queuedRecords: TRecord[];

    private processingId!: string;
    private internalIsBusy: boolean;
    private internalIsStopped: boolean;

    public constructor(processingFunction: ProcessingFunction<TRecord>, config?: TaskQueueConfig<TRecord>) {
        this.processingFunction = processingFunction;
        this.config = {
            interval: 1000,
            enableBatching: false,
            batchSize: 25,
            duplicateDetectionComparer: () => false,
            ...config
        };
        this.queuedRecords = [];

        this.internalIsBusy = false;
        this.internalIsStopped = false;
    }

    public get isBusy(): boolean {
        return this.internalIsBusy || this.queuedRecords.length > 0;
    }

    public get isStopped(): boolean {
        return this.internalIsStopped;
    }

    public startProcessing(): void {
        if (!this.processingId) {
            this.processingId = uuid();

            const data: IntervalStartedData = {
                action: this.nextTickAsync.bind(this),
                actionId: this.processingId,
                interval: this.config.interval
            };

            EventBus.emit(IntervalEvents.IntervalStarted, data);
        }
    }

    public stopProcessing(): void {
        if (!this.isStopped) {
            this.internalIsStopped = true;

            this.waitAsync()
                .then(() => EventBus.emit(IntervalEvents.IntervalEnded, <IntervalEndedData>{ actionId: this.processingId }));
        }
    }

    public waitAsync(): Promise<void> {
        return new Promise(resolve => {
            const waiterId = window.setInterval(() => {
                if (!this.isBusy) {
                    window.clearInterval(waiterId);
                    resolve();
                }
            }, this.config.interval / 2);
        });
    }

    public push(...records: TRecord[]): void {
        if (this.isStopped)
            throw new Error(`Task queue has been stopped.`);
        const notQueued = (record: TRecord) => not(this.duplicatesSomeOtherQueuedRecord(this.queuedRecords, record));
        const filtered = records.filter(notQueued);
        this.queuedRecords.push(...filtered);
    }

    private nextTickAsync(): Promise<void> {
        if (this.internalIsBusy || this.queuedRecords.length === 0)
            return Promise.resolve();

        this.markAsBusy();

        try {
            const recordsToProcess = this.queuedRecords.splice(0, this.queuedRecords.length);
            const taskSequence = this.config.enableBatching
                ? chunk(recordsToProcess, this.config.batchSize).reduce((promise, batch) => promise.then(() => this.resolveTaskAsync(batch)), Promise.resolve())
                : recordsToProcess.reduce((promise, record) => promise.then(() => this.resolveTaskAsync(record)), Promise.resolve());

            return taskSequence
                .then(() => this.markAsIdle())
                .catch(() => this.markAsIdle());
        } catch (err: unknown) {
            this.markAsIdle();

            throw err;
        }
    }

    private resolveTaskAsync(record: TRecord | TRecord[]): Promise<void> {
        return new Promise<void>(resolve => this.processingFunction(record, resolve));
    }

    private duplicatesSomeOtherQueuedRecord(queuedRecords: TRecord[], recrod: TRecord): boolean {
        return queuedRecords.some(queuedRecord => this.config.duplicateDetectionComparer(queuedRecord, recrod));
    }

    private markAsBusy(): void {
        this.internalIsBusy = true;
    }

    private markAsIdle(): void {
        this.internalIsBusy = false;
    }
}
